import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Shield, 
  ShieldCheck,
  Lock,
  Search,
  Lightbulb,
  Target,
  ArrowRight,
  CheckCircle,
  FileText,
  Users,
  AlertTriangle,
  Settings,
  BarChart3,
  RefreshCw,
  Database,
  GitBranch,
  Layers
} from "lucide-react";

const SouveraneCloud = () => {
  const { currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Enterprise Guarantee Cards - Based on Image 1
  const guaranteeCards = [
    {
      id: 'auditability',
      icon: Search,
      title: 'Auditability',
      description: 'Every decision logged. Every source tracked. Full chain of custody.',
      details: [
        'Immutable decision logs',
        'Source document versioning',
        'User action tracking',
        'Compliance-ready exports'
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'explainability',
      icon: Lightbulb,
      title: 'Explainability',
      description: 'Not just answers. Full reasoning path from question to conclusion.',
      details: [
        'Graph traversal visualization',
        'Step-by-step reasoning',
        'Source highlighting',
        'Confidence scoring'
      ],
      color: 'from-brand to-green-500'
    },
    {
      id: 'determinism',
      icon: Target,
      title: 'Determinism',
      description: 'Same query, same data = same answer. Every time. Guaranteed.',
      details: [
        'Reproducible results',
        'No hallucinations',
        'Testable & verifiable',
        'Regression-safe updates'
      ],
      color: 'from-purple-500 to-pink-500'
    }
  ];

  // ISO 42001 Requirements - Based on Image 2
  const isoRequirements = [
    { section: '§4', text: 'AI system context & stakeholder needs' },
    { section: '§5', text: 'Leadership & AI governance policies' },
    { section: '§6', text: 'Risk assessment & treatment' },
    { section: '§7', text: 'Resources, competence, documentation' },
    { section: '§8', text: 'AI system lifecycle controls' },
    { section: '§9', text: 'Performance evaluation & monitoring' },
    { section: '§10', text: 'Continual improvement' }
  ];

  // How AIWizz Complies - Based on Image 2
  const aiwizzCompliance = [
    { title: 'Data Provenance', text: 'Every input traced to source' },
    { title: 'Explainability', text: 'Graph paths document reasoning' },
    { title: 'Human Oversight', text: 'Confidence thresholds trigger review' },
    { title: 'Bias Detection', text: 'Knowledge graph auditable for gaps' },
    { title: 'Risk Management', text: 'Deterministic outputs, no hallucination' },
    { title: 'Incident Response', text: 'Full decision audit logs' },
    { title: 'Continuous Monitoring', text: 'Drift detection built-in' }
  ];

  const seoData = {
    title: 'KI-Souveränität | Private Enterprise LLM - BROVKO',
    description: 'Ihr privates Enterprise LLM mit 100% Datenhoheit. KI-Stack auf souveräner deutscher Infrastruktur.',
    keywords: 'KI Souveränität, Private LLM, Enterprise KI, DSGVO KI, Knowledge Graph, AIWizz',
    canonicalUrl: '/souveraene-cloud'
  };

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface">
      <SEOHead {...seoData} />
      <Header />

      {/* Hero Section - Ultra Compact */}
      <section className="pt-40 pb-6">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              KI-Souveränität: Ihr privates Enterprise LLM –{' '}
              <span className="bg-brand-accent bg-clip-text text-transparent">
                100% Datenhoheit, 0% Halluzinationen.
              </span>
            </h1>

            <p className={`text-base text-ink-faint mb-5 max-w-[62ch] transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Schützen Sie Ihr geistiges Eigentum. Wir implementieren Ihren KI-Stack auf einer souveränen deutschen Infrastruktur, unabhängig von US-Hyperscalern.
            </p>

            <Link to="/contact">
              <Button className="bg-brand hover:bg-brand-600 text-ink-onBrand px-5 py-2 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all group text-sm">
                <Lock className="mr-2 h-3.5 w-3.5" />
                Technisches Briefing anfordern
                <ArrowRight className="ml-2 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Enterprise-Grade Guarantees Section - Based on Image 1 */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-6">
            <span className="text-brand-400 text-xs uppercase tracking-widest font-medium">AIWizz.ai</span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mt-1">
              Enterprise-Grade <span className="text-brand-400">Guarantees</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {guaranteeCards.map((card) => (
              <div 
                key={card.id}
                className="bg-surface-raised/50 border border-hairline rounded-lg p-4 hover:border-teal-500/30 transition-all duration-300"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.color} bg-opacity-20 flex items-center justify-center mb-3`}>
                  <card.icon className="h-5 w-5 text-white" />
                </div>
                
                <h3 className="text-base font-bold text-white mb-1">{card.title}</h3>
                <p className="text-ink-faint text-xs mb-3">{card.description}</p>
                
                <ul className="space-y-1.5">
                  {card.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center gap-1.5 text-xs">
                      <CheckCircle className="h-3 w-3 text-brand-400 flex-shrink-0" />
                      <span className="text-brand-300">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Auditor Quote */}
          <div className="bg-slate-900/50 border border-hairline rounded-lg p-4 max-w-[62ch]">
            <p className="text-ink-muted text-xs">
              <span className="text-white font-medium">When the auditor asks:</span>{' '}
              <span className="italic text-ink-faint">"Why did the system recommend this?"</span>
            </p>
            <p className="text-brand-400 font-semibold mt-1 text-sm">
              You show the graph path. Not a prompt. Not a prayer.
            </p>
          </div>
        </div>
      </section>

      {/* ISO 42001 Ready Section - Based on Image 2 */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="text-center mb-5">
            <span className="text-brand-400 text-xs uppercase tracking-widest font-medium">AI Governance</span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mt-1">
              ISO <span className="text-brand-400">42001</span> Ready
            </h2>
            <p className="text-ink-faint text-xs mt-1">The international standard for AI Management Systems</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto mb-5">
            {/* What ISO 42001 Requires */}
            <div className="bg-surface-raised/50 border border-hairline rounded-lg p-4">
              <h3 className="text-base font-bold text-white mb-3">What ISO 42001 Requires</h3>
              <ul className="space-y-1.5">
                {isoRequirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs">
                    <span className="text-ink-faint font-mono w-7 flex-shrink-0">{req.section}</span>
                    <span className="text-ink-muted">{req.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* How AIWizz Complies */}
            <div className="bg-surface-raised/50 border border-hairline rounded-lg p-4">
              <h3 className="text-base font-bold text-white mb-3">How <span className="text-brand-400">AIWizz</span> Complies</h3>
              <ul className="space-y-1.5">
                {aiwizzCompliance.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 text-xs">
                    <CheckCircle className="h-3 w-3 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="text-brand-400 font-medium">{item.title}</span>
                      <span className="text-ink-faint"> — {item.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* EU AI Act Notice */}
          <div className="bg-slate-900/50 border border-hairline rounded-lg p-3 max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="text-ink-muted text-xs">
                  <span className="text-brand-400 font-semibold">EU AI Act</span> will require ISO 42001 for high-risk AI systems.
                </p>
                <p className="text-ink-faint text-xs mt-0.5">
                  Your compliance team will ask. AIWizz is ready. Generic Copilots are not.
                </p>
              </div>
              <div className="flex gap-2">
                <span className="bg-surface-raised border border-hairline text-ink-muted px-2 py-1 rounded text-xs font-medium">ISO 42001</span>
                <span className="bg-surface-raised border border-teal-500/50 text-brand-400 px-2 py-1 rounded text-xs font-medium">EU AI Act</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Section - Compact, No STACKIT */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-5">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white">
                Infrastruktur ohne Kompromisse
              </h2>
              <p className="text-ink-faint text-xs mt-1">Deutsche Rechenzentren. Keine US-Cloud-Abhängigkeit.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Datenstandort', value: 'Deutschland', icon: Database },
                { label: 'Cloud Act Exposure', value: '0%', icon: Shield },
                { label: 'Datenschutz-Level', value: 'Enterprise', icon: Lock },
                { label: 'DSGVO-konform', value: '100%', icon: CheckCircle }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-900/50 border border-hairline rounded-lg p-3 text-center">
                  <item.icon className="h-5 w-5 text-brand-400 mx-auto mb-1.5" />
                  <p className="text-ink-faint text-xs">{item.label}</p>
                  <p className="text-white font-bold text-base">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section - Compact */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4">
              Skalierbare Sicherheit
            </h2>
            
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              {[
                { icon: Database, title: 'Ihre Daten', desc: 'Isoliert & verschlüsselt' },
                { icon: GitBranch, title: 'Knowledge Graph', desc: 'Strukturiertes Wissen' },
                { icon: Layers, title: 'Private LLM', desc: 'Kein Datenabfluss' }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-800/30 border border-hairline rounded-lg p-3">
                  <item.icon className="h-6 w-6 text-brand-400 mx-auto mb-1.5" />
                  <h4 className="text-white font-semibold text-sm">{item.title}</h4>
                  <p className="text-ink-faint text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            <p className="text-ink-faint text-xs">
              <Link to="/aiwizz-plattform" className="text-brand-400 font-semibold hover:text-brand-300 transition-colors">
                AIWizz
              </Link> orchestriert Ihre Daten zu einem durchgängig auditierbaren System.
            </p>
          </div>
        </div>
      </section>

      {/* AIWizz Platform Teaser */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-teal-900/30 to-blue-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-teal-500/20">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand to-blue-500 rounded-xl flex items-center justify-center">
                    <Layers className="h-7 w-7 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg font-bold text-white mb-1">
                    Die Technologie hinter KI-Souveränität
                  </h3>
                  <p className="text-sm text-ink-muted">
                    AIWizz ist unsere bevorzugte Enterprise-Plattform für souveräne KI-Lösungen. 100% DSGVO-konform, in Deutschland gehostet.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link to="/aiwizz-plattform">
                    <Button className="bg-brand hover:bg-brand-600 text-ink-onBrand px-4 py-2 text-sm">
                      AIWizz entdecken
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch] text-center">
            <Shield className="h-10 w-10 text-brand-400 mx-auto mb-3" />
            
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              Sichern Sie sich Ihre KI-Souveränität.
            </h2>

            <p className="text-ink-faint text-sm mb-4">
              Technisches Executive Briefing für Ihre souveräne KI-Infrastruktur.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/contact">
                <Button className="bg-brand hover:bg-brand-600 text-ink-onBrand px-5 py-2 font-semibold rounded-lg group text-sm">
                  Technisches Executive Briefing anfordern
                  <ArrowRight className="ml-2 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/ki-value-check">
                <Button variant="outline" className="border-hairline-strong text-white hover:bg-surface-raised px-5 py-2 text-sm">
                  Zurück zum KI-Value Check
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SouveraneCloud;
